/**
 * SNES Composition Engine - Data Model Types
 *
 * All types are immutable by convention (readonly).
 * Projects are plain objects for easy YAML serialization.
 */

// =============================================================================
// Branded Types for Time Units
// =============================================================================

/**
 * Branded type for time values in beats.
 * Use `beats()` to create values and `asBeats()` to unwrap.
 */
export type Beats = number & { readonly __brand: 'beats' };

/**
 * Branded type for time values in bars.
 * Use `bars()` to create values and `asBars()` to unwrap.
 */
export type Bars = number & { readonly __brand: 'bars' };

/**
 * Create a Beats value from a number.
 */
export function beats(n: number): Beats {
  return n as Beats;
}

/**
 * Create a Bars value from a number.
 */
export function bars(n: number): Bars {
  return n as Bars;
}

/**
 * Unwrap a Beats value to a plain number.
 */
export function asBeats(b: Beats): number {
  return b as number;
}

/**
 * Unwrap a Bars value to a plain number.
 */
export function asBars(b: Bars): number {
  return b as number;
}

// =============================================================================
// Notes
// =============================================================================

/**
 * A single note in a clip.
 * Time and duration are in beats (relative to clip start).
 */
export interface Note {
  readonly pitch: number; // MIDI note number 0-127
  readonly time: Beats; // Start time in beats (relative to clip)
  readonly duration: Beats; // Duration in beats
  readonly velocity: number; // 0-127
}

// =============================================================================
// Clips
// =============================================================================

/**
 * A clip contains notes and lives on a track.
 * Start time and duration are in BEATS (not bars!).
 */
export interface Clip {
  readonly id: string;
  readonly start: Beats; // Start time in beats
  readonly duration: Beats; // Duration in beats
  readonly notes: readonly Note[];
}

// =============================================================================
// Automation
// =============================================================================

/**
 * A single automation point.
 * Time is in beats (global timeline).
 */
export interface AutomationPoint {
  readonly time: Beats; // Time in beats
  readonly value: number; // Normalized 0-1 or specific range
}

/**
 * An automation lane for a parameter.
 */
export interface AutomationLane {
  readonly parameter: string; // e.g., "volume", "pan", "echo.feedback"
  readonly points: readonly AutomationPoint[];
}

// =============================================================================
// Tracks
// =============================================================================

/**
 * A track holds clips and references an instrument.
 */
export interface Track {
  readonly id: string;
  readonly name: string;
  readonly instrumentId: string; // Reference to instrument
  readonly clips: readonly Clip[];
  readonly automation: readonly AutomationLane[];
  readonly mute: boolean;
  readonly solo: boolean;
}

// =============================================================================
// Instruments
// =============================================================================

/**
 * Velocity mapping defines what velocity controls.
 */
export type VelocityMap = 'volume' | 'filter' | 'attack' | 'custom';

/**
 * S-DSP instrument configuration.
 */
export interface SDSPInstrument {
  readonly id: string;
  readonly type: 'sdsp';
  readonly sampleId: string; // Reference to sample
  readonly adsr: readonly [number, number, number, number]; // A, D, S, SR (0-15, 0-7, 0-7, 0-31)
  readonly gainMode?: {
    readonly mode: 'direct' | 'linearDec' | 'expDec' | 'linearInc' | 'bentInc';
    readonly rate: number; // 0-31
    readonly value?: number; // 0-127 for direct mode
  };
  readonly velocityMap: VelocityMap;
  readonly volumeL?: number; // 0-127, default 127
  readonly volumeR?: number; // 0-127, default 127
}

/**
 * Union type for all instrument types.
 * Future: add other synth backends.
 */
export type Instrument = SDSPInstrument;

// =============================================================================
// Samples
// =============================================================================

/**
 * A symbolic reference to a game sample: $gameId[index]
 * Example: $a88[15] references sample 15 from U.N. Squadron
 */
export interface SymbolicSampleRef {
  readonly gameId: string;
  readonly index: number;
}

/**
 * An inline BRR sample with embedded data.
 */
export interface InlineSample {
  readonly id: string;
  readonly brr: string; // Base64-encoded BRR data
  readonly loopStart?: number; // Loop start in samples, undefined = no loop
  readonly baseNote: number; // MIDI note this sample is pitched at (e.g., 36 = C2)
  readonly sampleRate?: number; // Original sample rate, default 32000
}

/**
 * A symbolic sample reference that will be resolved at load time.
 */
export interface SymbolicSample {
  readonly id: string;
  readonly ref: SymbolicSampleRef; // e.g., { gameId: 'a88', index: 15 }
  readonly baseNote?: number; // MIDI note this sample is pitched at (auto-detected if omitted)
}

/**
 * A BRR sample definition - either inline data or a symbolic reference.
 */
export type Sample = InlineSample | SymbolicSample;

/**
 * Type guard for symbolic samples.
 */
export function isSymbolicSample(sample: Sample): sample is SymbolicSample {
  return 'ref' in sample;
}

/**
 * Type guard for inline samples.
 */
export function isInlineSample(sample: Sample): sample is InlineSample {
  return 'brr' in sample;
}

/**
 * Parse a symbolic sample reference string like "$a88[15]".
 * Returns null if the string doesn't match the pattern.
 */
export function parseSymbolicRef(ref: string): SymbolicSampleRef | null {
  const match = ref.match(/^\$([a-zA-Z0-9_-]+)\[(\d+)\]$/);
  if (!match) return null;
  return { gameId: match[1], index: parseInt(match[2], 10) };
}

/**
 * Format a symbolic sample reference as a string like "$a88[15]".
 */
export function formatSymbolicRef(ref: SymbolicSampleRef): string {
  return `$${ref.gameId}[${ref.index}]`;
}

// =============================================================================
// Global Automation
// =============================================================================

/**
 * Echo automation point.
 */
export interface EchoAutomationPoint {
  readonly time: Beats; // Time in beats
  readonly delay?: number; // Echo delay in ms
  readonly feedback?: number; // -1 to 1
  readonly volumeL?: number; // 0-127
  readonly volumeR?: number; // 0-127
  readonly fir?: readonly number[]; // 8 FIR coefficients
}

/**
 * Global automation settings.
 */
export interface GlobalAutomation {
  readonly echo?: readonly EchoAutomationPoint[];
  readonly masterVolume?: readonly AutomationPoint[];
}

// =============================================================================
// Project
// =============================================================================

/**
 * Time signature as [beats per bar, beat unit].
 * e.g., [4, 4] = 4/4 time
 */
export type TimeSignature = readonly [number, number];

/**
 * The root project structure.
 */
export interface Project {
  readonly name: string;
  readonly tempo: number; // BPM
  readonly timeSignature: TimeSignature;
  readonly samples: readonly Sample[];
  readonly instruments: readonly Instrument[];
  readonly tracks: readonly Track[];
  readonly globalAutomation: GlobalAutomation;
}

// =============================================================================
// Factory Functions
// =============================================================================

/**
 * Create an empty project with sensible defaults.
 */
export function createEmptyProject(name: string = 'Untitled'): Project {
  return {
    name,
    tempo: 120,
    timeSignature: [4, 4],
    samples: [],
    instruments: [],
    tracks: [],
    globalAutomation: {},
  };
}

/**
 * Create a note with defaults.
 */
export function createNote(
  pitch: number,
  time: number | Beats,
  duration: number | Beats = 1,
  velocity: number = 100
): Note {
  return {
    pitch,
    time: beats(time as number),
    duration: beats(duration as number),
    velocity,
  };
}

/**
 * Create a clip with defaults.
 */
export function createClip(
  id: string,
  start: number | Beats,
  duration: number | Beats,
  notes: readonly Note[] = []
): Clip {
  return {
    id,
    start: beats(start as number),
    duration: beats(duration as number),
    notes,
  };
}

/**
 * Create a track with defaults.
 */
export function createTrack(
  id: string,
  name: string,
  instrumentId: string,
  clips: readonly Clip[] = []
): Track {
  return {
    id,
    name,
    instrumentId,
    clips,
    automation: [],
    mute: false,
    solo: false,
  };
}

/**
 * Create an S-DSP instrument with defaults.
 */
export function createSDSPInstrument(
  id: string,
  sampleId: string,
  adsr: readonly [number, number, number, number] = [15, 7, 7, 20]
): SDSPInstrument {
  return {
    id,
    type: 'sdsp',
    sampleId,
    adsr,
    velocityMap: 'volume',
  };
}

// =============================================================================
// Utility Types
// =============================================================================

/**
 * Timing utilities for beat/bar/sample conversion.
 */
export interface TimingContext {
  readonly tempo: number;
  readonly timeSignature: TimeSignature;
  readonly sampleRate: number;
}

/**
 * Convert beats to seconds.
 */
export function beatsToSeconds(b: Beats, tempo: number): number {
  return (asBeats(b) / tempo) * 60;
}

/**
 * Convert seconds to beats.
 */
export function secondsToBeats(seconds: number, tempo: number): Beats {
  return beats((seconds * tempo) / 60);
}

/**
 * Convert bars to beats.
 */
export function barsToBeats(b: Bars, timeSignature: TimeSignature): Beats {
  return beats(asBars(b) * timeSignature[0]);
}

/**
 * Convert beats to bars.
 */
export function beatsToBars(b: Beats, timeSignature: TimeSignature): Bars {
  return bars(asBeats(b) / timeSignature[0]);
}

/**
 * Convert beats to samples.
 */
export function beatsToSamples(b: Beats, tempo: number, sampleRate: number): number {
  return beatsToSeconds(b, tempo) * sampleRate;
}

/**
 * Get beats per bar from time signature.
 */
export function getBeatsPerBar(timeSignature: TimeSignature): number {
  return timeSignature[0];
}

/**
 * Calculate the total duration of a project in beats.
 * Returns the end time of the last clip (including note durations).
 */
export function getProjectDuration(project: Project): Beats {
  let maxEndBeat = 0;

  for (const track of project.tracks) {
    for (const clip of track.clips) {
      // Clip end is start + duration
      const clipEnd = asBeats(clip.start) + asBeats(clip.duration);

      // Also check note end times (in case notes extend beyond clip duration)
      for (const note of clip.notes) {
        const noteEnd = asBeats(clip.start) + asBeats(note.time) + asBeats(note.duration);
        maxEndBeat = Math.max(maxEndBeat, noteEnd);
      }

      maxEndBeat = Math.max(maxEndBeat, clipEnd);
    }
  }

  return beats(maxEndBeat);
}
